import {useState, useCallback, useTransition} from 'react'
import {ChevronDownIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import styles from './CollapsiblePanel.module.css'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'

interface CollapsiblePanelProps {
  /** The heading text displayed in the panel header */
  heading: string
  /** Content rendered inside the collapsible body */
  children: React.ReactNode
  /** Optional action element rendered to the right of the heading (visible only when expanded) */
  headerAction?: React.ReactNode
  /** Additional class name applied to the panel's `<section>` element */
  className?: string
  /** Whether the panel is initially expanded (default: true) */
  defaultOpen?: boolean
}

/** A reusable collapsible section with a heading row and chevron toggle */
export function CollapsiblePanel({
  heading,
  children,
  headerAction,
  className,
  defaultOpen = true,
}: CollapsiblePanelProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [, startTransition] = useTransition()

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const toggle = useCallback(() => {
    sendPullRequestAnalyticsEvent('overview.section_toggle', 'OVERVIEW_COLLAPSIBLE_PANEL', {
      heading,
      is_open: !isOpen,
    })
    startTransition(() => {
      setIsOpen(prev => !prev)
    })
  }, [heading, isOpen, sendPullRequestAnalyticsEvent, startTransition])

  return (
    <section className={className}>
      <div className={styles.headerContainer}>
        <h2 className={styles.heading}>
          <button type="button" className={styles.headingRow} aria-expanded={isOpen} onClick={toggle}>
            <span>{heading}</span>
            <ChevronDownIcon size={16} className={clsx(styles.chevron, !isOpen && styles.chevronCollapsed)} />
          </button>
        </h2>
        {isOpen && headerAction}
      </div>
      {isOpen && children}
    </section>
  )
}
